import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Alert,
  Button,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { getAgentById } from "../../api/client";
import { getAgentLogs } from "../../api/client";
import AgentLogsTable from "../../components/Agents/AgentLogsTable";

const SKILL_LABELS = {
  receptionist: "Recepcionista",
  faq: "FAQ",
  ticket_seller: "Venda de ingressos",
  outros: "Outros",
};

function formatResponseTime(ms) {
  if (ms == null || !Number.isFinite(ms)) return "—";
  if (ms >= 1000) return `${(ms / 1000).toFixed(1).replace(".", ",")} s`;
  return `${ms} ms`;
}

const AgentAnalytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const { data: agentRes, isLoading: loadingAgent, error: agentError } = useQuery(
    ["agent", id],
    () => getAgentById(id),
    { enabled: !!id }
  );
  const agent = agentRes?.data ?? agentRes;

  const { data: logsRes, isLoading: loadingLogs } = useQuery(
    ["agent-logs", id, page + 1, pageSize],
    () => getAgentLogs(id, { page: page + 1, limit: pageSize }),
    { enabled: !!id }
  );
  const logs = useMemo(() => {
    const raw = logsRes?.data ?? logsRes ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [logsRes]);
  const pagination = logsRes?.pagination ?? {};
  const totalLogs = pagination?.total ?? logs.length;

  const stats = useMemo(() => {
    let totalResponseTime = 0;
    let countWithTime = 0;
    const skillCounts = {};
    logs.forEach((log) => {
      const skill = log.skill ?? log.skill_key ?? "outros";
      const label = SKILL_LABELS[skill] ?? skill;
      skillCounts[label] = (skillCounts[label] || 0) + 1;
      const ms = log.responseTimeMs ?? log.latencyMs ?? log.response_time_ms;
      if (ms != null && Number.isFinite(Number(ms))) {
        totalResponseTime += Number(ms);
        countWithTime++;
      }
    });
    const avgResponseTimeMs = countWithTime > 0 ? Math.round(totalResponseTime / countWithTime) : null;
    const skillsMostUsed = Object.entries(skillCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));
    return {
      totalInteractions: logs.length,
      avgResponseTimeMs,
      skillsMostUsed,
    };
  }, [logs]);

  if (!id) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography color="text.secondary">ID do agente de IA não informado.</Typography>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/agents")}>
          Voltar à lista
        </Button>
      </Box>
    );
  }

  if (loadingAgent && !agent) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Carregando...</Typography>
      </Box>
    );
  }

  if (agentError || !agent) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">Não foi possível carregar o agente de IA.</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/agents")}>
          Voltar à lista
        </Button>
      </Box>
    );
  }

  const agentName = agent.name ?? agent.nome ?? id;
  const hasLogs = logs.length > 0;

  return (
    <Box className="tour-agent-analytics" sx={{ paddingTop: 1 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate(`/agents/${id}`)}
        >
          Voltar
        </Button>
      </Stack>

      <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
        Estatísticas
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Resumo baseado nos logs carregados para o agente de IA <strong>{agentName}</strong>.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ height: "100%", borderColor: "divider" }}>
            <CardContent sx={{ "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <ChatBubbleOutlineRoundedIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Interações (página)
                </Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                {stats.totalInteractions}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totalLogs > logs.length ? `De ${totalLogs} no total` : "Nesta página"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ height: "100%", borderColor: "divider" }}>
            <CardContent sx={{ "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <SpeedRoundedIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Tempo médio de resposta
                </Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                {formatResponseTime(stats.avgResponseTimeMs)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.avgResponseTimeMs != null ? "Entre as interações desta página" : "Sem dados"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ height: "100%", borderColor: "divider" }}>
            <CardContent sx={{ "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <BarChartRoundedIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Funções mais usadas
                </Typography>
              </Box>
              {stats.skillsMostUsed.length === 0 ? (
                <Typography variant="body2" color="text.disabled">
                  Nenhuma interação ainda
                </Typography>
              ) : (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {stats.skillsMostUsed.map(({ name, count }) => (
                    <li key={name}>
                      <Typography variant="body2">
                        {name}: <strong>{count}</strong>
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Paper variant="outlined" sx={{ p: 2, borderColor: "divider" }}>
        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ChatBubbleOutlineRoundedIcon fontSize="small" />
          Logs de interação
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Histórico paginado das últimas interações com o agente de IA.
        </Typography>
        {!hasLogs && !loadingLogs ? (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              bgcolor: theme.palette.action.hover,
              borderRadius: 1,
            }}
          >
            <Typography color="text.secondary">Nenhum log de interação nesta página.</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              As interações aparecerão aqui quando o agente de IA for utilizado.
            </Typography>
          </Box>
        ) : (
          <AgentLogsTable
            logs={logs}
            isLoading={loadingLogs}
            page={page}
            pageSize={pageSize}
            rowCount={totalLogs}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={setPageSize}
          />
        )}
      </Paper>
    </Box>
  );
};

export default AgentAnalytics;
